import { Alert, Divider, Grid, List, Stack } from "@mui/material";
import MenuItem from "components/MenuItem";
import ActionLoader from "components/UI/ActionLoader";
import MuiOutlineButton from "components/UI/MuiOutlineButton";
import { fetchServices } from "framework/services";
import { Feature } from "models/api";
import { useQuery } from "react-query";
import LogoutIcon from "@mui/icons-material/Logout";
import useApp from "hooks/useApp";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { getAllPermission, getUserData } from "helpers";
import { IPermission } from "models/permissions";

interface Props {
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

export const viewable = (role: string, roles: string[]) => {
  return roles?.includes(role);
};

const DrawerMenu: React.FC<Props> = ({ setOpenDrawer }) => {

  const userData = getUserData();
  const { colors, roleName } =   userData;

  const [selectedFeature, setSelectedFeature] = React.useState<Feature | null>(
    null
  );
  const [permissions, setPermissions] = useState<IPermission[] | null>(null);
  const { data, isError, isLoading } = useQuery<Feature[]>(
    "features",
    fetchServices
  );
  let user = JSON.parse(localStorage.getItem("profile") || "{}");

  let features: Feature[] | undefined = user.IsAdmin
    ? data
    : data?.filter((feature) => !feature.IsAdmin && !feature?.hidden);

  // Debug: Check if Parameters feature is loaded and permissions - Force log immediately
  useEffect(() => {
    // Force immediate logging with alert to ensure visibility
    const debugInfo = {
      hasData: !!data, 
      hasPermissions: !!permissions,
      dataLength: data?.length || 0,
      permissionsLength: permissions?.length || 0,
      roleName: roleName
    };
    
    
    // if (data) {
    //   const paramFeature = data.find(f => f.slug === "parameter");
    //   console.log("🔎 Parameters Feature Check:", {
    //     featureFound: !!paramFeature,
    //     feature: paramFeature,
    //     allFeatures: data.map(f => ({ id: f.id, slug: f.slug, roles: f.roles }))
    //   });
    // }
    
    if (permissions) {
      const paramPermission = permissions.find(p => p.rolePermissionId === 61);
      const allParamPermissions = permissions.filter(p => 
        (p.permissionName && p.permissionName.toLowerCase().includes("parameter")) || 
        p.rolePermissionId === 61
      );
      
      // console.log("🔎 Permissions Check:", {
      //   permissionsCount: permissions.length,
      //   paramPermissionById: paramPermission,
      //   allParamRelatedPermissions: allParamPermissions,
      //   allRolePermissionIds: permissions.map(p => ({ 
      //     id: p.rolePermissionId, 
      //     name: p.permissionName,
      //     hasRead: p.hasReadPermission,
      //     roleId: p.roleId
      //   }))
      // });
    }
    
    if (data && permissions) {
      const paramFeature = data.find(f => f.slug === "parameter");
      const paramPermission = permissions.find(p => p.rolePermissionId === 61);
      
      console.log("🔎 Full Parameters Debug:", {
        featureFound: !!paramFeature,
        featureId: paramFeature?.id,
        featureRoles: paramFeature?.roles,
        roleName: roleName,
        permissionsCount: permissions.length,
        paramPermissionById: paramPermission,
        paramPermissionHasRead: paramPermission?.hasReadPermission,
        roleMatches: paramFeature ? viewable(roleName, paramFeature.roles) : false,
        willShow: paramPermission?.hasReadPermission && paramFeature && viewable(roleName, paramFeature.roles)
      });
    }
    console.log("=== DRAWERMENU DEBUG END ===");
  }, [data, permissions, roleName]);

  const { push } = useApp();
  const { t } = useTranslation();
  // let user: any | null = JSON.parse(localStorage.getItem("user")!);
  // if (!user) user = { name: "Administrator" }
  const handleLogout = () => {
    localStorage.clear();
    push("/login");
    return;
  };

  const fetchPermission = useCallback(async () => {
    try {
      const res = await getAllPermission();
      setPermissions(res);
    } catch (error: any) {}

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  // Debug: Log role and permissions
  // useEffect(() => {
  //   console.log("📋 Current User Info:", {
  //     roleName: roleName,
  //     allPermissions: permissions,
  //     parametersPermission: permissions?.find(p => p.rolePermissionId === 61)
  //   });
  // }, [roleName, permissions]);

  if (isError)
    return (
      <Alert severity="error">An error occurred while fetching features.</Alert>
    );
  if (isLoading) return <ActionLoader position="fixed" />;

  const handleSelectUnLinkFeature = (feature: Feature) =>
    setSelectedFeature(feature);

  const hasPermission = (feature: Feature) => {
    const permission = permissions?.find(
      (permission) => permission.rolePermissionId === feature.id
    );
    const hasRead = permission?.hasReadPermission;
    const roleMatches = viewable(roleName, feature.roles);
    
    // Debug logging for Parameters feature - ALWAYS log
    // if (feature.slug === "parameter") {
    //   console.log("🔍 Parameters Menu Debug:", {
    //     featureId: feature.id,
    //     featureSlug: feature.slug,
    //     featureRoles: feature.roles,
    //     currentRole: roleName,
    //     roleMatches: roleMatches,
    //     permissionFound: !!permission,
    //     permission: permission,
    //     hasReadPermission: hasRead,
    //     allPermissionsCount: permissions?.length || 0,
    //     allRolePermissionIds: permissions?.map(p => p.rolePermissionId) || [],
    //     willShow: hasRead && roleMatches,
    //     hasRead: hasRead
    //   });
    // }
    
    return hasRead && roleMatches;
  };

  return (
    <List>
      <Divider sx={{ mb: 1 }} />
      <Stack spacing={2}>
        {features &&
          features.length > 0 &&
          features.map((feature, index) => {
            // Debug: Log all features being checked
            // if (feature.slug === "parameter") {
            //   console.log("✅ Parameters feature found in map, checking permission...", feature);
            // }
            if (!hasPermission(feature) && feature.children.length === 0)
              return null;
            return(
            <MenuItem
              currPermissions={permissions}
              feature={feature}
              key={index}
              name={t(feature.name)}
              icon={feature.icon}
              color={"white"}
              slug={feature.slug}
              setOpenDrawer={setOpenDrawer}
              open={true}
              onSelectUnLinkFeature={handleSelectUnLinkFeature}
              hasChildren={feature.children.length > 0 ? true : false}
            />
          )})}
        <Grid container justifyContent={"center"}>
          <MuiOutlineButton
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<LogoutIcon sx={{ fill: "#232323" }} />}
            onClick={handleLogout}
          >
            Logout
          </MuiOutlineButton>
        </Grid>
      </Stack>
    </List>
  );
};

export default DrawerMenu;
